"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import {
  Sparkles,
  Bell,
  MessageCircle,
  ArrowLeft,
  ArrowRight,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * 2026-05-04 redesign phase 3 — subscribe-success walkthrough.
 *
 * Founder ask 2026-05-04 with screenshot: when a user subscribes to a
 * premium creator, show a 3-4 slide walkthrough explaining where the
 * feed is + how to interact with the creator.
 *
 * Trigger: SubscribeButton dispatches `granbay.subscribe.success` on
 * EVERY successful subscribe (renamed from the original
 * `subscribe.first` per founder direction "force the pop for the
 * subscriber everytime, give the user to tick to not see the pop
 * again"). The modal renders an inline "Don't show this again"
 * checkbox; the localStorage flag
 * `granbay.subscribe.walkthrough.seen` is only written when the
 * checkbox is ticked AND the user closes — i.e., user-explicit opt-
 * out instead of one-shot-then-hide. Default behavior: pop on every
 * subscribe until the user opts out.
 *
 * Backwards-compatible: still listens for the old
 * `granbay.subscribe.first` event so any in-flight callers don't
 * silently break during the rename window.
 *
 * Renders via createPortal to document.body so the SiteHeader's
 * backdrop-blur-md ancestor doesn't capture the fixed-positioned
 * overlay (CLAUDE.md 2026-05-04 backdrop-filter rule).
 */

// 2026-05-04 — bumped from v1 → v2 because PR #1410 (the original
// walkthrough) auto-wrote v1=true on every close, and the founder
// then changed direction in PR #1418 to "pop every time + explicit
// opt-out checkbox". Users who closed v1 even once carry a legacy
// v1=true flag that the new code (which still keys off the same
// localStorage entry) was respecting — so the modal silently never
// popped for them on subsequent subscribes. v2 keys are only ever
// written when the user explicitly ticks "don't show again", so
// existing closed-beta users start fresh under the new semantics.
const SEEN_KEY = "granbay.subscribe.walkthrough.seen.v2";

export function FirstSubscribeWalkthrough({
  creatorUsername,
}: {
  creatorUsername: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  // 2026-05-04 — explicit opt-out checkbox. Default false; checking
  // it + closing writes SEEN_KEY=true so future subscribes skip the
  // modal. Resets to false on every open so the user can leave it
  // unchecked (re-enable) on a subsequent subscribe even if they
  // ticked it last time and want to re-see the walkthrough.
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const onSuccess = (ev: Event) => {
      const detail = (ev as CustomEvent<{ username?: string }>).detail;
      // Only fire when this exact <SubscribeButton>'s creator is the one
      // the user just subscribed to — multiple SubscribeButtons can
      // mount per page (e.g. profile + post + sidebar).
      if (detail?.username !== creatorUsername) return;
      try {
        const seen = localStorage.getItem(SEEN_KEY);
        if (seen === "true") return; // user-explicit opt-out
      } catch {
        // Disabled storage — show anyway.
      }
      setDontShowAgain(false);
      setOpen(true);
      setStep(0);
    };
    // Both event names listened during the rename window.
    window.addEventListener("granbay.subscribe.success", onSuccess);
    window.addEventListener("granbay.subscribe.first", onSuccess);
    // 2026-05-04 — preview hook for QA. Visiting any /u/[username]
    // with `?preview-walkthrough=1` opens the modal regardless of
    // localStorage seen-flag or whether the user actually subscribed.
    // Preview close skips the seen-flag write so the real subscribe
    // flow still fires the modal afterward.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview-walkthrough") === "1") {
        setDontShowAgain(false);
        setOpen(true);
        setStep(0);
      }
    }
    return () => {
      window.removeEventListener("granbay.subscribe.success", onSuccess);
      window.removeEventListener("granbay.subscribe.first", onSuccess);
    };
  }, [creatorUsername]);

  function close() {
    setOpen(false);
    // Preview mode: don't burn the seen-flag so the real subscribe
    // flow still fires the modal afterward.
    let isPreview = false;
    if (typeof window !== "undefined") {
      isPreview =
        new URLSearchParams(window.location.search).get(
          "preview-walkthrough",
        ) === "1";
    }
    if (isPreview) return;
    if (!dontShowAgain) return; // pop again on next subscribe
    try {
      localStorage.setItem(SEEN_KEY, "true");
    } catch {
      // Disabled storage — modal will pop again next time anyway,
      // which matches the user's "don't show again was best-effort"
      // expectation when storage is off.
    }
  }

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const slides = [
    {
      icon: <Sparkles size={28} strokeWidth={1.75} aria-hidden="true" />,
      title: t(
        "subscribeWalkthrough.slide1Title",
        "Welcome to your premium feed",
      ),
      body: t(
        "subscribeWalkthrough.slide1Body",
        "You're subscribed to @{{username}}. Their subscriber-only posts now show on the Premium tab in /feed/posts and they appear at the top of your feed every time you open it.",
        { username: creatorUsername },
      ),
    },
    {
      icon: <Bell size={28} strokeWidth={1.75} aria-hidden="true" />,
      title: t("subscribeWalkthrough.slide2Title", "You'll get notified"),
      body: t(
        "subscribeWalkthrough.slide2Body",
        "When @{{username}} posts something new, you get a notification (bell icon, top-right). You can also enable push notifications in /settings/notifications.",
        { username: creatorUsername },
      ),
    },
    {
      icon: <MessageCircle size={28} strokeWidth={1.75} aria-hidden="true" />,
      title: t("subscribeWalkthrough.slide3Title", "Interact directly"),
      body: t(
        "subscribeWalkthrough.slide3Body",
        "Like, comment, and quote any of @{{username}}'s posts. Tip them with the heart-money button on premium posts. Your activity shows up under their profile and on the Following feed.",
        { username: creatorUsername },
      ),
    },
    {
      icon: <Sparkles size={28} strokeWidth={1.75} aria-hidden="true" />,
      title: t("subscribeWalkthrough.slide4Title", "Cancel anytime"),
      body: t(
        "subscribeWalkthrough.slide4Body",
        "Manage subscriptions in /portfolio/subscriptions. Cancel anytime — your access continues until the end of the current period. Welcome aboard.",
      ),
    },
  ];

  const last = slides.length - 1;
  const slide = slides[step];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-walkthrough-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{
        background: "color-mix(in srgb, var(--color-text) 60%, transparent)",
      }}
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label={t("subscribeWalkthrough.close", "Close")}
          className="absolute right-3 top-3 rounded-md p-1 text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <X size={16} strokeWidth={1.75} />
        </button>

        <div
          className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
          style={{
            background: "var(--color-brand-tint)",
            color: "var(--color-brand)",
          }}
        >
          {slide.icon}
        </div>

        <h2
          id="subscribe-walkthrough-title"
          className="mb-2 text-[18px] font-bold text-[var(--color-text)]"
        >
          {slide.title}
        </h2>
        <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-text2)]">
          {slide.body}
        </p>

        <div className="mb-4 flex justify-center gap-1.5" aria-hidden="true">
          {slides.map((_, i) => (
            <span
              key={i}
              className="h-1.5 w-6 rounded-full transition-colors"
              style={{
                background:
                  i === step ? "var(--color-brand)" : "var(--color-border2)",
              }}
            />
          ))}
        </div>

        {/* 2026-05-04 — explicit opt-out. Only renders on the last
            slide so the user has read the whole walkthrough before
            getting the option to suppress it. */}
        {step === last && (
          <label className="mb-4 flex cursor-pointer items-center gap-2 text-[12px] text-[var(--color-text2)]">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer"
            />
            {t("subscribeWalkthrough.dontShowAgain", "Don't show this again")}
          </label>
        )}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] disabled:opacity-40"
          >
            <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
            {t("subscribeWalkthrough.prev", "Back")}
          </button>

          {step < last ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(last, s + 1))}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[12px] font-bold text-white"
              style={{ background: "var(--color-brand)" }}
            >
              {t("subscribeWalkthrough.next", "Next")}
              <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          ) : (
            <Link
              href="/feed/posts"
              onClick={close}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[12px] font-bold text-white"
              style={{ background: "var(--color-brand)" }}
            >
              {t("subscribeWalkthrough.cta", "Go to my feed")}
              <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
